import { memo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Popover,
  PopoverBody,
  PopoverTitle,
  PopoverClose,
  PopoverHeader,
  PopoverFooter,
  PopoverContent,
  PopoverTrigger,
  PopoverDescription,
} from '@/components/ui/popover';

import { useRouter, usePathname } from 'src/routes/hooks';

import { useAuth } from 'src/features/auth/hooks/use-auth';

// ----------------------------------------------------------------------

type AccountPopoverProps = {
  data: {
    label: string;
    icon: React.ReactNode;
    href: string;
  }[];
  user: {
    displayName: string;
    email: string;
    photoURL: string;
  };
};

function AccountPopoverComponent({ data, user }: AccountPopoverProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { logout } = useAuth();

  const handleClickItem = useCallback(
    (path: string) => {
      router.push(path);
    },
    [router]
  );

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [logout]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="h-10 w-10 rounded-full p-0.5 bg-gradient-to-r from-primary via-yellow-400 to-primary">
          <Avatar className="h-full w-full">
            <AvatarImage src={user.photoURL} alt={user.displayName} />
            <AvatarFallback>{user.displayName.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" align="end" sideOffset={8}>
        <PopoverHeader>
          <div className="flex items-center space-x-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user.photoURL} alt={user.displayName} />
              <AvatarFallback>{user.displayName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col overflow-hidden">
              <PopoverTitle className="truncate">{user.displayName}</PopoverTitle>
              <PopoverDescription className="text-xs truncate">{user.email}</PopoverDescription>
            </div>
          </div>
        </PopoverHeader>
        <PopoverBody className="space-y-1 px-2 py-1">
          {data.map((option) => (
            <PopoverClose key={option.label} asChild>
              <Button
                variant="ghost"
                className={`w-full justify-start ${option.href === pathname ? 'bg-accent' : ''}`}
                size="sm"
                onClick={() => handleClickItem(option.href)}
              >
                <span className="mr-2 h-4 w-4">{option.icon}</span>
                {option.label}
              </Button>
            </PopoverClose>
          ))}
        </PopoverBody>
        <PopoverFooter>
          <PopoverClose asChild>
            <Button
              variant="outline"
              className="w-full bg-transparent text-destructive hover:text-destructive hover:bg-destructive/10"
              size="sm"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </PopoverClose>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}

export const AccountPopover = memo(AccountPopoverComponent);
